import { differenceInYears } from 'date-fns';
import { store } from 'src/store';

/**
 * @description Calculate the position and priority of the tile of type 'own-risk'.
 * @param {Object} tileObject
 * @returns {Object}
 */
export function calculateTileOwnRiskPositionPriorityAndVisibility(tileObject) {
  const visibilityBasedOnVisibilityParametres = calculateVisibility(tileObject);

  return {
    desktop: {
      visible: visibilityBasedOnVisibilityParametres,
      position: tileObject.priorityAndPosition.desktop.position,
      priority: tileObject.priorityAndPosition.desktop.priority,
    },
    mobile: {
      visible: visibilityBasedOnVisibilityParametres,
      position: tileObject.priorityAndPosition.mobile.position,
      priority: tileObject.priorityAndPosition.mobile.priority,
    },
  };
}

/**
 * @description Calculate the position and priority of the tile of type 'messages'.
 * @param {Object} tileObject
 * @returns {Object}
 */
export function calculateTileMessagesPositionPriorityAndVisibility(tileObject) {
  const hasUnreadDocumentsLast6Months = store.getters['document/hasUnreadDocumentsLast6Months'];
  const visibilityBasedOnVisibilityParametres = calculateVisibility(tileObject);
  return {
    desktop: {
      visible: visibilityBasedOnVisibilityParametres && hasUnreadDocumentsLast6Months,
      position: tileObject.priorityAndPosition.desktop.position,
      priority: tileObject.priorityAndPosition.desktop.priority,
    },
    mobile: {
      visible: visibilityBasedOnVisibilityParametres && hasUnreadDocumentsLast6Months,
      position: tileObject.priorityAndPosition.mobile.position,
      priority: tileObject.priorityAndPosition.mobile.priority,
    },
  };
}

/**
 * @description Calculate the position and priority of the tile of type 'open-invoices'.
 * @param {Object} tileObject
 * @returns {Object}
 */
export function calculateTileOpenInvoicesPositionPriorityAndVisibility(tileObject) {
  const payableInvoices = store.getters['invoice/payableInvoices'];
  const isLoading = store.getters['invoice/isLoadingInvoices'];
  const visibilityBasedOnVisibilityParametres = calculateVisibility(tileObject);
  const mayReadInvoice = store.getters['user/permissions'].mayReadInvoice;
  return {
    desktop: {
      visible: mayReadInvoice && visibilityBasedOnVisibilityParametres && payableInvoices.length > 0 && !isLoading,
      position: tileObject.priorityAndPosition.desktop.position,
      priority: tileObject.priorityAndPosition.desktop.priority,
    },
    mobile: {
      visible: mayReadInvoice && visibilityBasedOnVisibilityParametres && payableInvoices.length > 0 && !isLoading,
      position: tileObject.priorityAndPosition.mobile.position,
      priority: tileObject.priorityAndPosition.mobile.priority,
    },
  };
}

/**
 * @description Calculate the position and priority of the tile of type 'payment-arrangement'.
 * @param {Object} tileObject
 * @returns {Object}
 */
export function calculateTilePaymentArrangementPositionPriorityAndVisibility(tileObject) {
  const invoices = store.getters['invoice/paymentArrangementInvoices'];
  const isLoading = store.getters['invoice/isLoadingInvoices'];
  const visibilityBasedOnVisibilityParametres = calculateVisibility(tileObject);
  const mayReadInvoice = store.getters['user/permissions'].mayReadInvoice;
  return {
    desktop: {
      visible: mayReadInvoice && visibilityBasedOnVisibilityParametres && invoices.length > 0 && !isLoading,
      position: tileObject.priorityAndPosition.desktop.position,
      priority: tileObject.priorityAndPosition.desktop.priority,
    },
    mobile: {
      visible: mayReadInvoice && visibilityBasedOnVisibilityParametres && invoices.length > 0 && !isLoading,
      position: tileObject.priorityAndPosition.mobile.position,
      priority: tileObject.priorityAndPosition.mobile.priority,
    },
  };
}

/**
 * @description Calculate the position and priority of the tile of type 'good-news'.
 * @param {Object} tileObject
 * @returns {Object}
 */
export function calculateTileGoodNewsPositionPriorityAndVisibility(tileObject) {
  const payableInvoices = store.getters['invoice/payableInvoices'];
  const paymentArrangementInvoices = store.getters['invoice/paymentArrangementInvoices'];
  const count = payableInvoices.length + paymentArrangementInvoices.length;
  const isLoading = store.getters['invoice/isLoadingInvoices'];
  const visibilityBasedOnVisibilityParametres = calculateVisibility(tileObject);
  const mayReadInvoice = store.getters['user/permissions'].mayReadInvoice;
  return {
    desktop: {
      visible: mayReadInvoice && visibilityBasedOnVisibilityParametres && count === 0 && !isLoading,
      position: tileObject.priorityAndPosition.desktop.position,
      priority: tileObject.priorityAndPosition.desktop.priority,
    },
    mobile: {
      visible: mayReadInvoice && visibilityBasedOnVisibilityParametres && count === 0 && !isLoading,
      position: tileObject.priorityAndPosition.mobile.position,
      priority: tileObject.priorityAndPosition.mobile.priority,
    },
  };
}

/**
 * @description Calculate the position and priority of the tile of type 'ehic'.
 * @param {Object} tileObject
 * @returns {Object}
 */
export function calculateTileEhicPositionPriorityAndVisibility(tileObject) {
  const customerUser = store.getters['customer/customerUser'];
  const cards = store.getters['insuranceCard/digitalInsuranceCardInformation'];
  const visibilityBasedOnVisibilityParametres = calculateVisibility(tileObject);
  return {
    desktop: {
      visible: visibilityBasedOnVisibilityParametres && cards?.length > 0 && customerUser,
      position: tileObject.priorityAndPosition.desktop.position,
      priority: tileObject.priorityAndPosition.desktop.priority,
    },
    mobile: {
      visible: visibilityBasedOnVisibilityParametres && cards?.length > 0 && customerUser,
      position: tileObject.priorityAndPosition.mobile.position,
      priority: tileObject.priorityAndPosition.mobile.priority,
    },
  };
}

/**
 * @description Calculate the position and priority of the tile of type 'declaration'.
 * @param {Object} tileObject
 * @returns {Object}
 */
export function calculateTileDeclarationPositionPriorityAndVisibility(tileObject) {
  const processedStatus = 'Afgehandeld';
  const declarations = store.getters['declaration/declarationsPreview'];

  const visibilityBasedOnVisibilityParametres = calculateVisibility(tileObject);
  const visible =
    visibilityBasedOnVisibilityParametres &&
    declarations?.length !== 0 &&
    declarations.find((item) => processedStatus === item.status);
  return {
    desktop: {
      visible,
      position: tileObject.priorityAndPosition.desktop.position,
      priority: tileObject.priorityAndPosition.desktop.priority,
    },
    mobile: {
      visible,
      position: tileObject.priorityAndPosition.mobile.position,
      priority: tileObject.priorityAndPosition.mobile.priority,
    },
  };
}

/**
 * @description Calculate the position and priority of the tile of type 'insurance-right'.
 * @param {Object} tileObject
 * @returns {Object}
 */
export function calculateTileInsuranceRightPositionPriorityAndVisibility(tileObject) {
  const insuranceRight = store.getters['insuranceRight/insuranceRight'];
  const visibilityBasedOnVisibilityParametres = calculateVisibility(tileObject);
  const visible = visibilityBasedOnVisibilityParametres && insuranceRight?.isCheckRequired;
  return {
    desktop: {
      visible,
      position: tileObject.priorityAndPosition.desktop.position,
      priority: tileObject.priorityAndPosition.desktop.priority,
    },
    mobile: {
      visible,
      position: tileObject.priorityAndPosition.mobile.position,
      priority: tileObject.priorityAndPosition.mobile.priority,
    },
  };
}

/**
 * @description Calculate the position and priority of the tile of type 'tile-info-title'
 * @param {Object} tileObject
 * @returns {Object}
 */
export function calculateTileInfoTitlePositionPriorityAndVisibility(tileObject) {
  const visibilityBasedOnVisibilityParametres = calculateVisibility(tileObject);
  return {
    desktop: {
      visible: visibilityBasedOnVisibilityParametres,
      position: tileObject.priorityAndPosition.desktop.position,
      priority: tileObject.priorityAndPosition.desktop.priority,
    },
    mobile: {
      visible: visibilityBasedOnVisibilityParametres,
      position: tileObject.priorityAndPosition.mobile.position,
      priority: tileObject.priorityAndPosition.mobile.priority,
    },
  };
}

/**
 * @description Calculate the position and priority of the tile of type 'arrange-immediately'.
 * @param {Object} tileObject
 * @returns {Object}
 */
export function calculateTileArrangeImmediatelyPositionPriorityAndVisibility(tileObject) {
  const visibilityBasedOnVisibilityParametres = calculateVisibility(tileObject);
  return {
    desktop: {
      visible: visibilityBasedOnVisibilityParametres,
      position: tileObject.priorityAndPosition.desktop.position,
      priority: tileObject.priorityAndPosition.desktop.priority,
    },
    mobile: {
      visible: visibilityBasedOnVisibilityParametres,
      position: tileObject.priorityAndPosition.mobile.position,
      priority: tileObject.priorityAndPosition.mobile.priority,
    },
  };
}

/**
 * @description Calculate the position and priority of the tile of type 'tile-info-title-and-paragraph'.
 * @param {Object} tileObject
 * @returns {Object}
 */
export function calculateTileInfoTitleAndParagraphPositionPriorityAndVisibility(tileObject) {
  const visibilityBasedOnVisibilityParametres = calculateVisibility(tileObject);
  return {
    desktop: {
      visible: visibilityBasedOnVisibilityParametres,
      position: tileObject.priorityAndPosition.desktop.position,
      priority: tileObject.priorityAndPosition.desktop.priority,
    },
    mobile: {
      visible: visibilityBasedOnVisibilityParametres,
      position: tileObject.priorityAndPosition.mobile.position,
      priority: tileObject.priorityAndPosition.mobile.priority,
    },
  };
}

/**
 * @description Calculate the position and priority of the tile of type 'your-insurance'.
 * @param {Object} tileObject
 * @returns {Object}
 */
export function calculateTileYourInsurancePositionPriorityAndVisibility(tileObject) {
  const visibilityBasedOnVisibilityParametres = calculateVisibility(tileObject);
  return {
    desktop: {
      visible: visibilityBasedOnVisibilityParametres,
      position: tileObject.priorityAndPosition.desktop.position,
      priority: tileObject.priorityAndPosition.desktop.priority,
    },
    mobile: {
      visible: visibilityBasedOnVisibilityParametres,
      position: tileObject.priorityAndPosition.mobile.position,
      priority: tileObject.priorityAndPosition.mobile.priority,
    },
  };
}

/**
 * @description Calculate the position and priority of the tile of type 'fysio-self-check'.
 * @param {Object} tileObject
 * @returns {Object}
 */
export function calculateTileFysioSelfCheckPositionPriorityAndVisibility(tileObject) {
  const visibilityBasedOnVisibilityParametres = calculateVisibility(tileObject);
  return {
    desktop: {
      visible: visibilityBasedOnVisibilityParametres,
      position: tileObject.priorityAndPosition.desktop.position,
      priority: tileObject.priorityAndPosition.desktop.priority,
    },
    mobile: {
      visible: visibilityBasedOnVisibilityParametres,
      position: tileObject.priorityAndPosition.mobile.position,
      priority: tileObject.priorityAndPosition.mobile.priority,
    },
  };
}

/**
 * @description Calculate the position and priority of the tile of type 'damage-insurance'.
 * @param {Object} tileObject
 * @returns {Object}
 */
export function calculateTileDamageInsurancePositionPriorityAndVisibility(tileObject) {
  const visibilityBasedOnVisibilityParametres = calculateVisibility(tileObject);
  return {
    desktop: {
      visible: visibilityBasedOnVisibilityParametres,
      position: tileObject.priorityAndPosition.desktop.position,
      priority: tileObject.priorityAndPosition.desktop.priority,
    },
    mobile: {
      visible: visibilityBasedOnVisibilityParametres,
      position: tileObject.priorityAndPosition.mobile.position,
      priority: tileObject.priorityAndPosition.mobile.priority,
    },
  };
}

/**
 * @description Calculate the position and priority of the tile of type 'healthcare-contracts'.
 * @param {Object} tileObject
 * @returns {Object}
 */
export function calculateTileHealthcareContractsPositionPriorityAndVisibility(tileObject) {
  const visibilityBasedOnVisibilityParametres = calculateVisibility(tileObject);
  return {
    desktop: {
      visible: visibilityBasedOnVisibilityParametres,
      position: tileObject.priorityAndPosition.desktop.position,
      priority: tileObject.priorityAndPosition.desktop.priority,
    },
    mobile: {
      visible: visibilityBasedOnVisibilityParametres,
      position: tileObject.priorityAndPosition.mobile.position,
      priority: tileObject.priorityAndPosition.mobile.priority,
    },
  };
}

/**
 * @description Calculate the position and priority of the tile of type 'tile-info-title-and-paragraph'.
 * @param {Object} tileObject
 * @returns {Object}
 */
export function calculateRequestPolicyPositionPriorityAndVisibility(tileObject) {
  const visibilityBasedOnVisibilityParametres = calculateVisibility(tileObject);
  return {
    desktop: {
      visible: visibilityBasedOnVisibilityParametres,
      position: tileObject.priorityAndPosition.desktop.position,
      priority: tileObject.priorityAndPosition.desktop.priority,
    },
    mobile: {
      visible: visibilityBasedOnVisibilityParametres,
      position: tileObject.priorityAndPosition.mobile.position,
      priority: tileObject.priorityAndPosition.mobile.priority,
    },
  };
}

/**
 * @description Shared logic to determine visibility based on the tileObject.visibilityParametres.
 * @param {Object} tileObject
 * @returns {boolean}
 */
function calculateVisibility(tileObject) {
  const visibilityParametres = tileObject.visibilityParametres;
  if (!visibilityParametres) return true;

  return (
    calculateVisibilityStartDate(visibilityParametres.startDate) &&
    calculateVisibilityEndDate(visibilityParametres.endDate) &&
    calculateVisibilityExcludedTypeInsured(visibilityParametres.excludedTypeInsured) &&
    calculateVisibilityExlusiveCollectivities(visibilityParametres.exlusiveCollectivities) &&
    calculateVisibilityExclusiveLabels(visibilityParametres.exclusiveLabels) &&
    calculateVisibilityRequiresActivePolicy(visibilityParametres.requiresActivePolicy) &&
    calculateVisibilityExcludedProducts(visibilityParametres.excludedProducts) &&
    calculateVisibilityRequiresProducts(visibilityParametres.requiresProducts) &&
    calculateVisibilityUserIsNotDefaulter(visibilityParametres.checkUserIsDefaulter) &&
    calculateVisibilityRequiresAdult(visibilityParametres.requiresAdult) &&
    calculateVisibilityOfferYear(visibilityParametres.requiresOfferYear)
  );
}

/**
 * @description Shared logic to determine visibility based on the startDate.
 * @param {String} startDateString
 * @returns {boolean}
 */
function calculateVisibilityStartDate(startDateString) {
  if (!startDateString) return true;
  // Set the hours to 0 to compare only the date
  const startDate = new Date(startDateString).setHours(0, 0, 0, 0);
  return startDate <= new Date().setHours(0, 0, 0, 0);
}

/**
 * @description Shared logic to determine visibility based on the endDate.
 * @param {String} endDateString
 * @returns {boolean}
 */
function calculateVisibilityEndDate(endDateString) {
  if (!endDateString) return true;
  // Set the hours to 0 to compare only the date
  const endDate = new Date(endDateString).setHours(0, 0, 0, 0);
  return endDate > new Date().setHours(0, 0, 0, 0);
}

/**
 * @description Shared logic to determine visibility based on the excludedTypeInsured.
 * @param {Array} excludedTypeInsured
 * @returns {boolean}
 */
function calculateVisibilityExcludedTypeInsured(excludedTypeInsured) {
  if (!excludedTypeInsured || excludedTypeInsured.length === 0) return true;
  const typeInsured = store.getters['user/user'].role.Role;
  return !excludedTypeInsured.includes(typeInsured);
}

/**
 * @description Shared logic to determine visibility based on the exlusiveCollectivities.
 * @param {Array} exlusiveCollectivities
 * @returns {boolean}
 */
function calculateVisibilityExlusiveCollectivities(exlusiveCollectivities) {
  if (!exlusiveCollectivities || exlusiveCollectivities.length === 0) return true;
  const agreementDetails = store.getters['agreement/agreementDetails'];
  const contractCodes = agreementDetails?.map((agreement) => agreement.contractCode);
  return exlusiveCollectivities.some((collectivity) => contractCodes?.includes(collectivity));
}

function calculateVisibilityExclusiveLabels(exclusiveLabels) {
  if (!exclusiveLabels || exclusiveLabels.length === 0) return true;
  const labels = store.getters['user/user'].collectivityLabel.collectivityLabel;
  return exclusiveLabels.some((label) => labels.includes(label));
}

/**
 * @description Shared logic to determine visibility based on the requiresActivePolicy.
 * @param {boolean} requiresActivePolicy
 * @returns {boolean}
 */
function calculateVisibilityRequiresActivePolicy(requiresActivePolicy) {
  if (!requiresActivePolicy) return true;
  const agreementDetails = store.getters['agreement/agreementDetails'];
  return agreementDetails?.some((agreement) => !agreement.hasAgreementEnded);
}

/**
 * @description Shared logic to determine visibility based on the checkUserIsDefaulter.
 * @param {*} checkUserIsDefaulter
 * @returns
 */
function calculateVisibilityUserIsNotDefaulter(checkUserIsDefaulter) {
  if (!checkUserIsDefaulter) return true;
  const user = store.getters['user/user'];
  return !user.isDefaulter;
}

/**
 * @description Shared logic to determine visibility based on the requiresProducts.
 * @param {*} requiresProducts
 * @returns
 */
function calculateVisibilityRequiresProducts(requiresProducts) {
  if (!requiresProducts || requiresProducts.length === 0) return true;

  return getProductsByUser()?.some((x) => requiresProducts.some((y) => y === x.typeOfProduct));
}

function calculateVisibilityRequiresAdult(requiresAdult) {
  if (!requiresAdult) return true;
  const user = store.getters['user/user'];
  const now = new Date();
  return differenceInYears(now, new Date(user.birthDate)) >= 18;
}

function calculateVisibilityExcludedProducts(excludedProducts) {
  if (!excludedProducts || excludedProducts.length === 0) return true;

  return getProductsByUser().filter((x) => excludedProducts.includes(x.typeOfProduct))?.length === 0;
}

function calculateVisibilityOfferYear(requiresOfferYear) {
  if (!requiresOfferYear || requiresOfferYear === 0) return true;
  const offerYear = store.getters['agreement/offerYear'];
  if (!offerYear || offerYear === 0 || offerYear !== requiresOfferYear) return false;
  return true;
}

function getProductsByUser() {
  const user = store.getters['user/user'];
  let agreementDetails = store.getters['agreement/agreementDetailsForOfferYear'];
  if (!agreementDetails) {
    agreementDetails = store.getters['agreement/agreementDetails'];
  }
  if (!agreementDetails || agreementDetails.length === 0) return [];
  const policyNumber = agreementDetails[0].agreementParticipants.find(
    (x) => x.customer.customerNumber === user.customerNumber,
  )?.policyNumber;
  return agreementDetails
    ?.filter((x) => !x.hasAgreementEnded)
    .flatMap((x) => x.policyDetails)
    .find((x) => x.policyNumber === policyNumber).products;
}
